
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { themes } from '../../assets/themes';
import { RootState } from '../store';
import { loadStateFromLocalStorage, processClientCode, saveStateToLocalStorage } from '../../utils';
import { getDashboardTheme, getSelectedBusinessDashboardTheme } from '../actions/businessActions'; // Import the async thunk

export type ThemeName = keyof typeof themes;
type ThemeMode = 'light' | 'dark';

// Business dashboard theme flattened properties
export interface BusinessDashboardThemeData {
  businessId?: string | null;
  clientCode: string;
  version: number;
  themeName?: string | null;
  description?: string | null;
  uploadedAt?: string | null;
}

export interface BusinessDashboardThemeResponse {
  businessId?: string;
  clientCode: string;
  version: number;
  theme: typeof themes[ThemeName];
  themeName?: string;
  description?: string | null;
  uploadedAt?: any;
}

interface ThemeState {
  // Original states - KEPT EXACTLY AS THEY WERE
  textTheme: ThemeName; // Optional: Keep original textTheme if you still need it for static themes
  activeTheme: ThemeName;
  mode: ThemeMode;
  theme: typeof themes[ThemeName][ThemeMode];
  
  // ============== FLATTENED BUSINESS THEME PROPERTIES ==============
  businessId: string | null;
  businessClientCode: string | null;
  businessThemeVersion: number;
  businessThemeName: string | null;
  businessDescription: string | null;
  businessUploadedAt: string | null;
  
  // ============== RAW BUSINESS THEME OBJECT ==============
  businessThemeRaw: typeof themes[ThemeName] | null;
  
  // ============== LOADING & ERROR STATES ==============
  businessThemeLoading: boolean;
  businessThemeError: string | null;
  businessThemeSuccess: boolean;


}

const initialState: ThemeState = {
  // Original states
  textTheme: 'elisyan', // Optional: Set default text theme to match default active theme
  activeTheme: 'elisyan',
  mode: 'light',
  theme: themes.elisyan.light,
  
  // ============== FLATTENED BUSINESS THEME PROPERTIES ==============
  businessId: null,
  businessClientCode: null,
  businessThemeVersion: 0,
  businessThemeName: null,
  businessDescription: null,
  businessUploadedAt: null,
  
  // ============== RAW BUSINESS THEME OBJECT ==============
  businessThemeRaw: null,
  
  // ============== LOADING & ERROR STATES ==============
  businessThemeLoading: false,
  businessThemeError: null,
  businessThemeSuccess: false,

  
};

// Load persisted state
const loadedState = loadStateFromLocalStorage<Partial<ThemeState>>('themeState');

// Load flattened business theme properties from localStorage
const loadBusinessThemeFromStorage = () => {
  const saved = loadStateFromLocalStorage<Partial<ThemeState>>('businessThemeState');
  return {
    businessId: saved?.businessId || null,
    businessClientCode: saved?.businessClientCode || 'elisyan',
    businessThemeVersion: saved?.businessThemeVersion || 0,
    businessThemeName: saved?.businessThemeName || null,
    businessDescription: saved?.businessDescription || null,
    businessUploadedAt: saved?.businessUploadedAt || null,
    businessThemeRaw: saved?.businessThemeRaw || null,
  };
};

const persistedState: ThemeState = {
  // Original states
  textTheme: initialState.textTheme,
  activeTheme:  initialState.activeTheme,
  mode: loadedState?.mode || initialState.mode,
  theme: loadedState?.theme && loadedState?.mode 
    ? loadedState.theme 
    : initialState.theme,
  
  // Flattened business theme properties
  ...loadBusinessThemeFromStorage(),
  
  // Loading states (don't persist)
  businessThemeLoading: false,
  businessThemeError: null,
  businessThemeSuccess: false,
  
};

const themeSlice = createSlice({
  name: 'theme',
  initialState: persistedState,
  reducers: {

    logoutTheme: () => {
      saveStateToLocalStorage('businessThemeState', null);
      return { ...initialState };
    },
    
    toggleMode: (state) => {
      state.mode = state.mode === 'light' ? 'dark' : 'light';
      state.theme = themes[state.activeTheme][state.mode];
      saveStateToLocalStorage('themeState', { 
        activeTheme: state.activeTheme, 
        mode: state.mode,
        theme: state.theme
      });
    },
    
    setTheme: (state, action: PayloadAction<{ theme: ThemeName; mode: ThemeMode }>) => {
      state.activeTheme = action.payload.theme;
      state.mode = action.payload.mode;
      state.theme = themes[action.payload.theme][action.payload.mode];
      saveStateToLocalStorage('themeState', { 
        activeTheme: state.activeTheme, 
        mode: state.mode,
        theme: state.theme
      });
    },
    
    // ============== FLATTENED BUSINESS THEME REDUCERS ==============
    setBusinessTheme: (state, action: PayloadAction<BusinessDashboardThemeResponse | null>) => {
      if (action.payload) {
        // Update flattened properties
        state.businessId = action.payload.businessId || null;
        state.businessClientCode = action.payload.clientCode?.toLowerCase() || 'elisyan';
        state.businessThemeVersion = action.payload.version || 0;
        state.businessThemeName = action.payload.themeName || `Theme v${action.payload.version}`;
        state.businessDescription = action.payload.description || null;
        state.businessUploadedAt = action.payload.uploadedAt || new Date().toISOString();
        
        // Update raw business theme object
        state.businessThemeRaw = action.payload.theme || null;
        
        console.log(`🎨 Business theme set for ${state.businessClientCode} v${state.businessThemeVersion}`);
      } else {
        // Clear all business theme properties
        state.businessId = null;
        state.businessClientCode = 'elisyan';
        state.businessThemeVersion = 0;
        state.businessThemeName = null;
        state.businessDescription = null;
        state.businessUploadedAt = null;
        state.businessThemeRaw = null;
        
        console.log(`🎨 Business theme cleared`);
      }
      
      // Save entire flattened state to localStorage
      saveStateToLocalStorage('businessThemeState', {
        businessId: state.businessId,
        businessClientCode: state.businessClientCode,
        businessThemeVersion: state.businessThemeVersion,
        businessThemeName: state.businessThemeName,
        businessDescription: state.businessDescription,
        businessUploadedAt: state.businessUploadedAt,
        businessThemeRaw: state.businessThemeRaw,
      });
    },
    
    setBusinessThemeVersion: (state, action: PayloadAction<number>) => {
      state.businessThemeVersion = action.payload;
      saveStateToLocalStorage('businessThemeState', {
        ...state,
        businessThemeVersion: action.payload,
      });
    },
    
    setBusinessThemeRaw: (state, action: PayloadAction<typeof themes[ThemeName] | null>) => {
      state.businessThemeRaw = action.payload;
      saveStateToLocalStorage('businessThemeState', {
        ...state,
        businessThemeRaw: action.payload,
      });
    },
    
    clearBusinessTheme: (state) => {
      // Reset all properties individually
      state.businessId = null;
      state.businessClientCode = 'elisyan';
      state.businessThemeVersion = 0;
      state.businessThemeName = null;
      state.businessDescription = null;
      state.businessUploadedAt = null;
      state.businessThemeRaw = null;
      
      saveStateToLocalStorage('businessThemeState', null);
    },
    
    // Batch update multiple properties
    updateBusinessTheme: (state, action: PayloadAction<Partial<BusinessDashboardThemeData & { businessThemeRaw?: typeof themes[ThemeName] | null }>>) => {
      Object.assign(state, action.payload);
      saveStateToLocalStorage('businessThemeState', {
        businessId: state.businessId,
        businessClientCode: state.businessClientCode,
        businessThemeVersion: state.businessThemeVersion,
        businessThemeName: state.businessThemeName,
        businessDescription: state.businessDescription,
        businessUploadedAt: state.businessUploadedAt,
        businessThemeRaw: state.businessThemeRaw,
      });
    },
  },
  
  // ============== EXTRA REDUCERS FOR ASYNC THUNKS ==============
  extraReducers: (builder) => {
    // Get Dashboard Theme
    builder
      .addCase(getDashboardTheme.pending, (state) => {
        state.businessThemeLoading = true;
        state.businessThemeError = null;
        state.businessThemeSuccess = false;
        console.log(`⏳ Fetching dashboard theme...`);
      })
      .addCase(getDashboardTheme.fulfilled, (state, action) => {
        state.businessThemeLoading = false;
        state.businessThemeSuccess = true;
        state.businessThemeError = null;

        const themeData = action.payload.data;

        // Update state directly with the fetched theme data
        state.businessId = themeData.businessId || null;
        state.businessClientCode = themeData.clientCode?.toLowerCase() || 'elisyan';
        state.businessThemeVersion = themeData.version || 0;
        state.businessThemeName = themeData.themeName || `Theme v${themeData.version}`;
        state.businessDescription = themeData.description || null;
        state.businessUploadedAt = themeData.uploadedAt || new Date().toISOString();
        state.businessThemeRaw = themeData.theme || null;
        state.businessThemeVersion= themeData.version || 0;
        state.theme = themeData.theme ? themeData.theme.light : themes.elisyan.light; // Optional: Update main theme if business theme is set, or reset to default if null
        state.activeTheme = themeData.theme ? processClientCode(themeData.theme.name) as ThemeName : 'elisyan'; // Optional: Update active theme to match business client code, or reset to default if null
         // Save entire flattened state to localStorage
         saveStateToLocalStorage('themeState', { 
          activeTheme: state.activeTheme, 
          mode: state.mode,
          theme: state.theme
        });
      saveStateToLocalStorage('businessThemeState', {
        businessId: state.businessId,
        businessClientCode: state.businessClientCode,
        businessThemeVersion: state.businessThemeVersion,
        businessThemeName: state.businessThemeName,
        businessDescription: state.businessDescription,
        businessUploadedAt: state.businessUploadedAt,
        businessThemeRaw: state.businessThemeRaw,
      });

        console.log(`🎨 Business theme set for ${state.businessClientCode} v${state.businessThemeVersion}`);
      })
      .addCase(getDashboardTheme.rejected, (state, action) => {
        state.businessThemeLoading = false;
        state.businessThemeSuccess = false;
        state.businessThemeError = action.payload as string || 'Failed to fetch dashboard theme';
        
        // If theme not found, clear business theme
        if (action.payload === "THEME_NOT_FOUND") {
          console.log(`⚠️ Theme not found, clearing business theme`);
          state.businessId = null;
          state.businessClientCode = 'elisyan';
          state.businessThemeVersion = 0;
          state.businessThemeName = null;
          state.businessDescription = null;
          state.businessUploadedAt = null;
          state.businessThemeRaw = null;
        }
        
        console.error(`❌ Failed to fetch dashboard theme:`, action.payload);
      });
  },
});

// Export all actions
export const { 
  toggleMode, 
  setTheme, 
  logoutTheme,
  setBusinessTheme,
  setBusinessThemeVersion,
  setBusinessThemeRaw,
  clearBusinessTheme,
  updateBusinessTheme,
} = themeSlice.actions;

// ============== ORIGINAL SELECTORS - UNCHANGED ==============
export const selectState = (state: RootState) => state.theme_slice;

// ============== FLATTENED BUSINESS THEME SELECTORS ==============

export const selectBusinessThemeVersion = (state: RootState) => state.theme_slice.businessThemeVersion;
export const selectBusinessClientCode = (state: RootState) => state.theme_slice.businessClientCode;
export const selectBusinessThemeName = (state: RootState) => state.theme_slice.businessThemeName;
export const selectBusinessDescription = (state: RootState) => state.theme_slice.businessDescription;
export const selectBusinessUploadedAt = (state: RootState) => state.theme_slice.businessUploadedAt;
export const selectBusinessId = (state: RootState) => state.theme_slice.businessId;

// ============== SELECTORS FOR LOADING STATES ==============
export const selectBusinessThemeLoading = (state: RootState) => state.theme_slice.businessThemeLoading;
export const selectBusinessThemeError = (state: RootState) => state.theme_slice.businessThemeError;
export const selectBusinessThemeSuccess = (state: RootState) => state.theme_slice.businessThemeSuccess;

// ============== SELECTOR FOR RAW BUSINESS THEME OBJECT ==============
export const selectBusinessThemeRaw = (state: RootState) => state.theme_slice.businessThemeRaw;

// Composite selector for when you need the whole object
export const selectBusinessTheme = (state: RootState) => ({
  businessId: state.theme_slice.businessId,
  clientCode: state.theme_slice.businessClientCode,
  version: state.theme_slice.businessThemeVersion,
  description: state.theme_slice.businessDescription,
  uploadedAt: state.theme_slice.businessUploadedAt,
  theme: state.theme_slice.businessThemeRaw,
});

export const selectHasBusinessTheme = (state: RootState) => 
  state.theme_slice.businessThemeVersion > 0 && 
  state.theme_slice.businessClientCode !== 'elisyan';

export const selectBusinessThemeObject = (state: RootState) => state.theme_slice.businessThemeRaw;

export default themeSlice.reducer;





















// import { createSlice, PayloadAction } from '@reduxjs/toolkit';
// import { themes } from '../../assets/themes';
// import { RootState } from '../store';
// import { loadStateFromLocalStorage, processClientCode, saveStateToLocalStorage } from '../../utils';
// import { getDashboardTheme } from '../actions/businessActions';

// export type ThemeName = keyof typeof themes;
// type ThemeMode = 'light' | 'dark';

// // Business dashboard theme flattened properties
// export interface BusinessDashboardThemeData {
//   businessId?: string | null;
//   clientCode: string;
//   version: number;
//   themeName?: string | null;
//   description?: string | null;
//   uploadedAt?: string | null;
// }

// export interface BusinessDashboardThemeResponse {
//   businessId?: string;
//   clientCode: string;
//   version: number;
//   theme?: typeof themes[ThemeName] | null; // Made optional
//   themeName?: string;
//   description?: string | null;
//   uploadedAt?: any;
// }

// interface ThemeState {
//   // Original states
//   textTheme: ThemeName;
//   activeTheme: ThemeName;
//   mode: ThemeMode;
//   theme: typeof themes[ThemeName][ThemeMode] | null; // Allow null for safety
  
//   // ============== FLATTENED BUSINESS THEME PROPERTIES ==============
//   businessId: string | null;
//   businessClientCode: string;
//   businessThemeVersion: number;
//   businessThemeName: string | null;
//   businessDescription: string | null;
//   businessUploadedAt: string | null;
  
//   // ============== RAW BUSINESS THEME OBJECT ==============
//   businessThemeRaw: typeof themes[ThemeName] | null;
  
//   // ============== LOADING & ERROR STATES ==============
//   businessThemeLoading: boolean;
//   businessThemeError: string | null;
//   businessThemeSuccess: boolean;
// }

// // SAFELY get default theme
// const getDefaultTheme = () => {
//   return themes.elisyan.light;
// };

// const defaultTheme = getDefaultTheme();

// const initialState: ThemeState = {
//   // Original states
//   textTheme: 'elisyan',
//   activeTheme: 'elisyan',
//   mode: 'light',
//   theme: themes?.elisyan?.light,
  
//   // ============== FLATTENED BUSINESS THEME PROPERTIES ==============
//   businessId: null,
//   businessClientCode: 'elisyan',
//   businessThemeVersion: 0,
//   businessThemeName: null,
//   businessDescription: null,
//   businessUploadedAt: null,
  
//   // ============== RAW BUSINESS THEME OBJECT ==============
//   businessThemeRaw: null,
  
//   // ============== LOADING & ERROR STATES ==============
//   businessThemeLoading: false,
//   businessThemeError: null,
//   businessThemeSuccess: false,
// };

// // Load persisted state with error handling
// const loadPersistedState = () => {
//   try {
//     return loadStateFromLocalStorage<Partial<ThemeState>>('themeState') || {};
//   } catch (error) {
//     console.error('Error loading themeState:', error);
//     return {};
//   }
// };

// // Load flattened business theme properties from localStorage
// const loadBusinessThemeFromStorage = () => {
//   try {
//     const saved = loadStateFromLocalStorage<Partial<ThemeState>>('businessThemeState');
//     return {
//       businessId: saved?.businessId || null,
//       businessClientCode: saved?.businessClientCode || 'elisyan',
//       businessThemeVersion: saved?.businessThemeVersion || 0,
//       businessThemeName: saved?.businessThemeName || null,
//       businessDescription: saved?.businessDescription || null,
//       businessUploadedAt: saved?.businessUploadedAt || null,
//       businessThemeRaw: saved?.businessThemeRaw || null,
//     };
//   } catch (error) {
//     console.error('Error loading businessThemeState:', error);
//     return {
//       businessId: null,
//       businessClientCode: 'elisyan',
//       businessThemeVersion: 0,
//       businessThemeName: null,
//       businessDescription: null,
//       businessUploadedAt: null,
//       businessThemeRaw: null,
//     };
//   }
// };

// const loadedState = loadPersistedState();
// const businessThemeFromStorage = loadBusinessThemeFromStorage();

// // Safely compute theme from loaded state
// const computeThemeFromState = () => {
//   try {
//     if (loadedState?.activeTheme && loadedState?.mode) {
//       const themeObj = themes[loadedState.activeTheme as ThemeName];
//       if (themeObj && themeObj[loadedState.mode as ThemeMode]) {
//         return themeObj[loadedState.mode as ThemeMode];
//       }
//     }
//   } catch (error) {
//     console.error('Error computing theme from state:', error);
//   }
//   return defaultTheme;
// };

// const persistedState: ThemeState = {
//   // Original states
//   textTheme: initialState.textTheme,
//   activeTheme: (loadedState?.activeTheme as ThemeName) || initialState.activeTheme,
//   mode: (loadedState?.mode as ThemeMode) || initialState.mode,
//   theme: computeThemeFromState(),
  
//   // Flattened business theme properties
//   ...businessThemeFromStorage,
  
//   // Loading states (don't persist)
//   businessThemeLoading: false,
//   businessThemeError: null,
//   businessThemeSuccess: false,
// };

// const themeSlice = createSlice({
//   name: 'theme',
//   initialState: persistedState,
//   reducers: {
//     // ============== ORIGINAL REDUCERS - WITH SAFETY CHECKS ==============
//     logoutTheme: () => {
//       saveStateToLocalStorage('businessThemeState', null);
//       return { ...initialState };
//     },
    
//     toggleMode: (state) => {
//       try {
//         state.mode = state.mode === 'light' ? 'dark' : 'light';
//         const themeObj = themes[state.activeTheme];
//         if (themeObj && themeObj[state.mode]) {
//           state.theme = themeObj[state.mode];
//         }
//         saveStateToLocalStorage('themeState', { 
//           activeTheme: state.activeTheme, 
//           mode: state.mode 
//         });
//       } catch (error) {
//         console.error('Error in toggleMode:', error);
//       }
//     },
    
//     setTheme: (state, action: PayloadAction<{ theme: ThemeName; mode: ThemeMode }>) => {
//       try {
//         state.activeTheme = action.payload.theme;
//         state.mode = action.payload.mode;
//         const themeObj = themes[action.payload.theme];
//         if (themeObj && themeObj[action.payload.mode]) {
//           state.theme = themeObj[action.payload.mode];
//         }
//         saveStateToLocalStorage('themeState', { 
//           activeTheme: state.activeTheme, 
//           mode: state.mode 
//         });
//       } catch (error) {
//         console.error('Error in setTheme:', error);
//       }
//     },
    
//     // ============== FLATTENED BUSINESS THEME REDUCERS ==============
//     setBusinessTheme: (state, action: PayloadAction<BusinessDashboardThemeResponse | null>) => {
//       try {
//         if (action.payload) {
//           // Update flattened properties
//           state.businessId = action.payload.businessId || null;
//           state.businessClientCode = action.payload.clientCode?.toLowerCase() || 'elisyan';
//           state.businessThemeVersion = action.payload.version || 0;
//           state.businessThemeName = action.payload.themeName || `Theme v${action.payload.version}`;
//           state.businessDescription = action.payload.description || null;
//           state.businessUploadedAt = action.payload.uploadedAt || new Date().toISOString();
          
//           // Update raw business theme object
//           state.businessThemeRaw = action.payload.theme || null;
          
//           console.log(`🎨 Business theme set for ${state.businessClientCode} v${state.businessThemeVersion}`);
//         } else {
//           // Clear all business theme properties
//           state.businessId = null;
//           state.businessClientCode = 'elisyan';
//           state.businessThemeVersion = 0;
//           state.businessThemeName = null;
//           state.businessDescription = null;
//           state.businessUploadedAt = null;
//           state.businessThemeRaw = null;
          
//           console.log(`🎨 Business theme cleared`);
//         }
        
//         // Save entire flattened state to localStorage
//         saveStateToLocalStorage('businessThemeState', {
//           businessId: state.businessId,
//           businessClientCode: state.businessClientCode,
//           businessThemeVersion: state.businessThemeVersion,
//           businessThemeName: state.businessThemeName,
//           businessDescription: state.businessDescription,
//           businessUploadedAt: state.businessUploadedAt,
//           businessThemeRaw: state.businessThemeRaw,
//         });
//       } catch (error) {
//         console.error('Error in setBusinessTheme:', error);
//       }
//     },
    
//     setBusinessThemeVersion: (state, action: PayloadAction<number>) => {
//       state.businessThemeVersion = action.payload;
//       saveStateToLocalStorage('businessThemeState', {
//         ...state,
//         businessThemeVersion: action.payload,
//       });
//     },
    
//     setBusinessThemeRaw: (state, action: PayloadAction<typeof themes[ThemeName] | null>) => {
//       state.businessThemeRaw = action.payload;
//       saveStateToLocalStorage('businessThemeState', {
//         ...state,
//         businessThemeRaw: action.payload,
//       });
//     },
    
//     clearBusinessTheme: (state) => {
//       state.businessId = null;
//       state.businessClientCode = 'elisyan';
//       state.businessThemeVersion = 0;
//       state.businessThemeName = null;
//       state.businessDescription = null;
//       state.businessUploadedAt = null;
//       state.businessThemeRaw = null;
      
//       saveStateToLocalStorage('businessThemeState', null);
//     },
    
//     updateBusinessTheme: (state, action: PayloadAction<Partial<BusinessDashboardThemeData & { businessThemeRaw?: typeof themes[ThemeName] | null }>>) => {
//       Object.assign(state, action.payload);
//       saveStateToLocalStorage('businessThemeState', {
//         businessId: state.businessId,
//         businessClientCode: state.businessClientCode,
//         businessThemeVersion: state.businessThemeVersion,
//         businessThemeName: state.businessThemeName,
//         businessDescription: state.businessDescription,
//         businessUploadedAt: state.businessUploadedAt,
//         businessThemeRaw: state.businessThemeRaw,
//       });
//     },
//   },
  
//   // ============== EXTRA REDUCERS FOR ASYNC THUNKS ==============
//   extraReducers: (builder) => {
//     builder
//       .addCase(getDashboardTheme.pending, (state) => {
//         state.businessThemeLoading = true;
//         state.businessThemeError = null;
//         state.businessThemeSuccess = false;
//       })
//       .addCase(getDashboardTheme.fulfilled, (state, action) => {
//         state.businessThemeLoading = false;
//         state.businessThemeSuccess = true;
//         state.businessThemeError = null;

//         try {
//           const themeData = action.payload?.data;
//           if (!themeData) {
//             console.error('No theme data in payload');
//             return;
//           }

//           // Update business theme properties
//           state.businessId = themeData.businessId || null;
//           state.businessClientCode = themeData.clientCode?.toLowerCase() || 'elisyan';
//           state.businessThemeVersion = themeData.version || 0;
//           state.businessThemeName = themeData.themeName || `Theme v${themeData.version}`;
//           state.businessDescription = themeData.description || null;
//           state.businessUploadedAt = themeData.uploadedAt || new Date().toISOString();
//           state.businessThemeRaw = themeData.theme || null;

//           // ============== SAFELY UPDATE MAIN THEME ==============
//           // Only update if themeData.theme exists and has light/dark properties
//           if (themeData.theme) {
//             // Update activeTheme based on client code
//             if (themeData.clientCode) {
//               const processedCode = processClientCode(themeData.clientCode) as ThemeName;
//               // Check if this theme exists in your themes object
//               if (themes[processedCode]) {
//                 state.activeTheme = processedCode;
//               }
//             }

//             // Update theme with light/dark mode - with null checks
//             if (themeData.theme.light) {
//               state.theme = themeData.theme.light;
//             } else if (themes[state.activeTheme]?.[state.mode]) {
//               // Fallback to existing theme
//               state.theme = themes[state.activeTheme][state.mode];
//             }
//           }

//           // Save to localStorage
//           saveStateToLocalStorage('themeState', { 
//             activeTheme: state.activeTheme, 
//             mode: state.mode 
//           });
          
//           saveStateToLocalStorage('businessThemeState', {
//             businessId: state.businessId,
//             businessClientCode: state.businessClientCode,
//             businessThemeVersion: state.businessThemeVersion,
//             businessThemeName: state.businessThemeName,
//             businessDescription: state.businessDescription,
//             businessUploadedAt: state.businessUploadedAt,
//             businessThemeRaw: state.businessThemeRaw,
//           });

//           console.log(`🎨 Business theme set for ${state.businessClientCode} v${state.businessThemeVersion}`);
//         } catch (error) {
//           console.error('Error processing fulfilled theme:', error);
//         }
//       })
//       .addCase(getDashboardTheme.rejected, (state, action) => {
//         state.businessThemeLoading = false;
//         state.businessThemeSuccess = false;
//         state.businessThemeError = action.payload as string || 'Failed to fetch dashboard theme';
        
//         if (action.payload === "THEME_NOT_FOUND") {
//           console.log(`⚠️ Theme not found, clearing business theme`);
//           state.businessId = null;
//           state.businessClientCode = 'elisyan';
//           state.businessThemeVersion = 0;
//           state.businessThemeName = null;
//           state.businessDescription = null;
//           state.businessUploadedAt = null;
//           state.businessThemeRaw = null;
//         }
//       });
//   },
// });

// // Export all actions
// export const { 
//   toggleMode, 
//   setTheme, 
//   logoutTheme,
//   setBusinessTheme,
//   setBusinessThemeVersion,
//   setBusinessThemeRaw,
//   clearBusinessTheme,
//   updateBusinessTheme,
// } = themeSlice.actions;

// // ============== ORIGINAL SELECTORS ==============
// export const selectState = (state: RootState) => state.theme_slice;

// // ============== FLATTENED BUSINESS THEME SELECTORS ==============
// export const selectBusinessThemeVersion = (state: RootState) => state.theme_slice.businessThemeVersion;
// export const selectBusinessClientCode = (state: RootState) => state.theme_slice.businessClientCode;
// export const selectBusinessThemeName = (state: RootState) => state.theme_slice.businessThemeName;
// export const selectBusinessDescription = (state: RootState) => state.theme_slice.businessDescription;
// export const selectBusinessUploadedAt = (state: RootState) => state.theme_slice.businessUploadedAt;
// export const selectBusinessId = (state: RootState) => state.theme_slice.businessId;

// // ============== SELECTORS FOR LOADING STATES ==============
// export const selectBusinessThemeLoading = (state: RootState) => state.theme_slice.businessThemeLoading;
// export const selectBusinessThemeError = (state: RootState) => state.theme_slice.businessThemeError;
// export const selectBusinessThemeSuccess = (state: RootState) => state.theme_slice.businessThemeSuccess;

// // ============== SELECTOR FOR RAW BUSINESS THEME OBJECT ==============
// export const selectBusinessThemeRaw = (state: RootState) => state.theme_slice.businessThemeRaw;

// // Composite selector for when you need the whole object
// export const selectBusinessTheme = (state: RootState) => ({
//   businessId: state.theme_slice.businessId,
//   clientCode: state.theme_slice.businessClientCode,
//   version: state.theme_slice.businessThemeVersion,
//   description: state.theme_slice.businessDescription,
//   uploadedAt: state.theme_slice.businessUploadedAt,
//   theme: state.theme_slice.businessThemeRaw,
// });

// export const selectHasBusinessTheme = (state: RootState) => 
//   state.theme_slice.businessThemeVersion > 0 && 
//   state.theme_slice.businessClientCode !== 'elisyan';

// export default themeSlice.reducer;